import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import {
  establishConfirmationSession,
  parseAuthEmailCallback,
} from '../../lib/authCallback';
import { AuthCallbackProgress, AuthCallbackShell } from './AuthCallbackShell';
import { AuthReturnActions } from './AuthReturnActions';

const VERIFY_WATCHDOG_MS = 25_000;

type ConfirmView =
  | { status: 'verifying' }
  | { status: 'success' }
  | { status: 'error'; message: string };

/**
 * Browser-only email confirmation — finite states, no auto app bounce.
 */
export function EmailConfirmedPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<ConfirmView>({ status: 'verifying' });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    let settled = false;
    const watchdog = window.setTimeout(() => {
      if (!settled) {
        setView({
          status: 'error',
          message:
            'This is taking too long. The link may be expired or your connection dropped. Open MovieMe and try signing in, or request a fresh confirmation email.',
        });
      }
    }, VERIFY_WATCHDOG_MS);

    (async () => {
      const params = parseAuthEmailCallback();
      const result = await establishConfirmationSession(params);
      settled = true;
      window.clearTimeout(watchdog);

      if (result.ok) {
        setView({ status: 'success' });
      } else {
        setView({
          status: 'error',
          message:
            result.message ||
            'This confirmation link is invalid, expired, or already used. Open MovieMe and try signing in — if that fails, resend confirmation from the signup screen.',
        });
      }
    })();

    return () => {
      window.clearTimeout(watchdog);
    };
  }, []);

  const goToWebsiteSignIn = () => {
    navigate('/', { replace: true });
  };

  if (view.status === 'verifying') {
    return (
      <AuthCallbackShell liveStatus="Confirming your email">
        <AuthCallbackProgress label="Confirming your email…" />
      </AuthCallbackShell>
    );
  }

  if (view.status === 'error') {
    return (
      <AuthCallbackShell liveStatus="Confirmation failed">
        <div className="space-y-6 text-center">
          <XCircle className="mx-auto h-14 w-14 text-red-400" aria-hidden />
          <div className="space-y-2">
            <h1 className="display text-2xl font-semibold text-foreground">
              This link didn’t work
            </h1>
            <p id="confirm-error" className="leading-relaxed text-muted-foreground">
              {view.message}
            </p>
          </div>
          <AuthReturnActions
            openAppLabel="Open MovieMe app"
            secondaryLabel="Sign in on the website"
            onSecondary={goToWebsiteSignIn}
            supportingCopy="If sign-in fails, resend confirmation from the signup screen."
          />
        </div>
      </AuthCallbackShell>
    );
  }

  return (
    <AuthCallbackShell liveStatus="Email confirmed">
      <div className="space-y-6 text-center">
        <CheckCircle className="mx-auto h-14 w-14 text-emerald-400" aria-hidden />
        <div className="space-y-2">
          <h1 className="display text-2xl font-semibold text-foreground">Email confirmed</h1>
          <p className="leading-relaxed text-muted-foreground">
            Your account is ready. Return to MovieMe and sign in.
          </p>
        </div>
        <AuthReturnActions onSecondary={goToWebsiteSignIn} />
      </div>
    </AuthCallbackShell>
  );
}
